import {sendEvent} from '@github-ui/hydro-analytics'
import {Tooltip} from '@github-ui/pacer/Tooltip'
import {
  CodeIcon,
  FileDiffIcon,
  ImageIcon,
  IssueClosedIcon,
  IssueDraftIcon,
  IssueOpenedIcon,
  MarkdownIcon,
  XIcon,
} from '@primer/octicons-react'
import {IconButton, Spinner} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {useLayoutEffect, useRef, useState} from 'react'

import type {File, PreviewableContent} from './content-preview-types'
import styles from './ContentPreviewTab.module.css'

interface ContentPreviewTabProps extends React.HTMLAttributes<HTMLButtonElement> {
  isActive?: boolean
  isEphemeral?: boolean
  onClose?: () => void
  item: PreviewableContent
  controlHintId: string
  tabPanelId?: string
  ref?: React.Ref<HTMLDivElement>
}

function CodeTabIcon({file, className}: {file: File; className?: string}) {
  if (file.isStreaming) {
    return <Spinner size="small" />
  }
  switch (file.language) {
    case 'markdown':
      return <MarkdownIcon className={className} />
    default:
      return <CodeIcon className={className} />
  }
}

function TabIcon({item}: {item: PreviewableContent}) {
  switch (item.type) {
    case 'file':
      return <CodeTabIcon className={styles.activeTabIcon} file={item} />
    case 'file-diff':
      return <FileDiffIcon className={styles.activeTabIcon} />
    case 'changeset-diff':
      return <FileDiffIcon className={styles.activeTabIcon} />
    case 'image':
      return <ImageIcon className={styles.activeTabIcon} />
    case 'issue':
      return item.state === 'closed' ? (
        <IssueClosedIcon className={'fgColor-done'} />
      ) : (
        <IssueOpenedIcon className={item.state === 'open' ? 'fgColor-open' : undefined} />
      )
    case 'new-issue':
      return <IssueDraftIcon />
    case 'update-issue':
      return <IssueDraftIcon />
    case 'existing-issue':
      return <IssueOpenedIcon />
    case 'plugin-content':
      return item.icon ? <item.icon /> : null
    default:
      return null
  }
}

export function ContentPreviewTab({
  ref,
  onClick,
  onClose,
  onDoubleClick,
  isActive,
  isEphemeral,
  item,
  controlHintId,
  tabPanelId,
  ...rest
}: ContentPreviewTabProps) {
  const [isTitleTruncated, setIsTitleTruncated] = useState(false)
  const titleRef = useRef<HTMLSpanElement>(null)
  const showCloseButton = !item.disableCloseButton

  const closeTab = () => {
    if (onClose) onClose()
    sendEvent('dotcom_chat.activate', {target: 'BROWSER_TAB_CLOSE', mode: 'immersive'})
  }

  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const title = item.name

  const checkTruncation = () => {
    const element = titleRef.current
    if (!element) return

    const isOverflowing = element.scrollWidth > element.clientWidth
    setIsTitleTruncated(prev => (isOverflowing !== prev ? isOverflowing : prev))
  }

  useLayoutEffect(() => {
    checkTruncation()
  }, [title])

  const titleSpan = (
    <span
      className={clsx(styles.title, {[styles.isClipped]: isTitleTruncated, [styles.isEphemeral]: isEphemeral})}
      data-title={title}
      data-testid={`content-preview-tab-${item.id}`}
      ref={titleRef}
      onMouseEnter={checkTruncation}
    >
      {title}
    </span>
  )

  return (
    <div ref={ref} className={styles.tabContainer}>
      <div
        className={clsx(styles.tabContent, {
          [styles.isActive]: isActive,
        })}
      >
        <button
          className={styles.mainButton}
          type="button"
          role="tab"
          aria-selected={isActive ? 'true' : undefined}
          aria-describedby={controlHintId}
          aria-controls={tabPanelId}
          onClick={e => {
            if (onClick) onClick(e)
            sendEvent('dotcom_chat.activate', {target: 'BROWSER_TAB_SELECT', mode: 'immersive'})
          }}
          onDoubleClick={onDoubleClick}
          onKeyDown={e => {
            switch (e.key.toLowerCase()) {
              case 'delete':
              case 'backspace':
                if (showCloseButton) closeTab()
                break
              case 'tab':
                if (e.shiftKey) break
                if (showCloseButton) {
                  e.preventDefault()
                  closeButtonRef.current?.focus()
                }
            }
          }}
          onAuxClick={e => {
            // middle mouse button
            if (e.button === 1 && showCloseButton) closeTab()
          }}
          {...rest}
        >
          <span className={styles.icon}>
            <TabIcon item={item} />
          </span>
          {/* The data-title attribute and respective CSS is required to set the width of the title using a semibold
          font-weight. This ensures that switching between tabs with different font-weight won't change the width
          of a tab because a thicker font-weight adds to the width of the element. */}
          {isTitleTruncated ? (
            <Tooltip text={title} position="bottom">
              {titleSpan}
            </Tooltip>
          ) : (
            titleSpan
          )}
          {item.trailingComponent ? <div className={styles.trailingComponent}>{item.trailingComponent}</div> : null}
        </button>
        {showCloseButton && (
          <div className={styles.closeButton}>
            <IconButton
              icon={XIcon}
              size="small"
              variant="invisible"
              aria-label={`Close ${title}`}
              tabIndex={-1}
              onClick={closeTab}
              ref={closeButtonRef}
            />
          </div>
        )}
      </div>
    </div>
  )
}
